import React, { useContext, useMemo } from 'react';
import { SalvCard, SalvTooltipWrapper, Size } from '@salvapp/ui-components';
import { useAssignCase, useQueryCaseStatuses, useUnassignCase } from './caseManagementQueryActions';
import CaseStatusDropdown from './CaseStatusDropdown';
import { InvestigationAssignmentDropdown } from '../../alerts/InvestigationAssignmentDropdown';
import Has from '../../auth/Has';
import { CAN_MODIFY_CASE } from '../../auth/permissions';
import { CasePendingStatusContext } from './CasePendingStatusContext';
import { InternalApiCase } from '../../api-internal';

interface Props {
  caseData: InternalApiCase;
}

const CaseManagement = ({ caseData: { handle, assignedStatus, assignment } }: Props) => {
  const { setPendingStatus } = useContext(CasePendingStatusContext)!;

  const { data: statuses, isPending: isStatusPending, isError: isStatusError } = useQueryCaseStatuses();

  const assignCase = useAssignCase(handle);
  const unassignCase = useUnassignCase(handle);

  const currentStatus = useMemo(() => {
    if (isStatusPending || isStatusError) return undefined;
    return statuses.find((s) => s.code === assignedStatus.code);
  }, [statuses, assignedStatus.code, isStatusPending, isStatusError]);

  return (
    <SalvCard className='case-management-status-assignment'>
      <SalvCard.Body style={{ display: 'flex', alignItems: 'end', justifyContent: 'space-between' }}>
        <div className='status-block'>
          <div className='caps-xs p-b-4' style={{ color: 'var(--secondary-default)' }}>
            Case Status
          </div>
          <Has allPermissions={[CAN_MODIFY_CASE]}>
            {(can: boolean) => (
              <SalvTooltipWrapper tooltip='Set status' shortcut={'S'}>
                <CaseStatusDropdown
                  label={currentStatus?.textName ?? 'Set status'}
                  size={Size.MEDIUM}
                  disabled={!can}
                  assignedStatusCode={assignedStatus.code}
                  shortcutTriggerKey={'s'}
                  onStatusSelect={setPendingStatus}
                />
              </SalvTooltipWrapper>
            )}
          </Has>
        </div>
        {!isStatusPending && (
          <Has allPermissions={[CAN_MODIFY_CASE]}>
            <SalvTooltipWrapper tooltip='Assign user' shortcut={'A'}>
              <InvestigationAssignmentDropdown
                assignee={
                  assignment?.assignee
                    ? { name: assignment.assignee.name, email: assignment.assignee.email }
                    : undefined
                }
                onChange={(user) => assignCase.mutate(user.id!)}
                onUnassign={() => unassignCase.mutate()}
                size={Size.MEDIUM}
                shortcutTriggerKey={'a'}
                anyPermissions={[CAN_MODIFY_CASE]}
              />
            </SalvTooltipWrapper>
          </Has>
        )}
      </SalvCard.Body>
    </SalvCard>
  );
};

export default CaseManagement;
